import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";

const newRequestId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm" style={{ borderBottom: "1px solid var(--border)" }}>
    <span style={{ color: "var(--text-muted)" }}>{label}</span><span className="font-semibold text-right" data-testid={testid}>{children}</span>
  </div>
);

// Confirmation step before any points are deducted; one request id per opening blocks duplicate redemptions
export default function RedeemConfirmDialog({ open, member, points, notes, onCancel, onDone }) {
  const [pv, setPv] = useState(1);
  const [busy, setBusy] = useState(false);
  const reqId = useRef(null);
  useEffect(() => {
    if (!open) return;
    reqId.current = newRequestId();
    api.get("/config").then(({ data }) => setPv(parseFloat(data.point_value_aed) || 1)).catch(() => {});
  }, [open]);
  if (!member) return null;
  const available = (member.reward_balance || 0) - (member.held_points || 0);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/rewards/redeem", {
        customer_id: member.customer_id, points, notes: notes || null, confirmed: true, request_id: reqId.current,
      });
      toast.success(`Redemption approved: −${points} pts · new balance ${data.new_balance}`);
      onDone?.(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel?.(); }}>
      <AlertDialogContent data-testid="redeem-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" style={{ color: "var(--gold)" }} />Confirm redemption</AlertDialogTitle>
          <AlertDialogDescription>Points are deducted immediately and the redemption is recorded as approved under your name.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mt-1">
          <Row label="Customer" testid="redeem-confirm-name">{member.name}</Row>
          <Row label="Member ID" testid="redeem-confirm-id"><span className="font-mono">{member.customer_id}</span></Row>
          <Row label="Points to redeem" testid="redeem-confirm-points">{points.toLocaleString()} pts</Row>
          <Row label="Redemption value" testid="redeem-confirm-value">AED {(points * pv).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Row>
          <Row label="Balance after" testid="redeem-confirm-balance-after">{(available - points).toLocaleString()} pts</Row>
          {notes && <Row label="Notes">{notes}</Row>}
        </div>
        <AlertDialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel} data-testid="redeem-confirm-cancel">Cancel</Button>
          <Button type="button" disabled={busy} onClick={confirm} className="bg-navy text-white" data-testid="redeem-confirm-approve">
            {busy ? "Processing…" : "Confirm & Approve"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
